'use client';

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { LayoutGrid, FileText, Printer, Download, Info } from 'lucide-react';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { BriefingSidebar } from './briefing-sidebar';
import { BriefingTypeBadge } from './briefing-type-badge';
import './briefing-print-styles.css';
import { BriefingTileView } from './briefing-tile-view';
import { BriefingFlowView } from './briefing-flow-view';
import { parseBriefingSections } from '@/lib/briefing-sections';
import { BRIEFING_TYPES, type BriefingType } from '@/types/briefing-type';
import { getBriefingViewMessages, type UiLanguage } from '@/i18n/briefing-view';

// ---------------------------------------------------------------------------
// View mode storage
// ---------------------------------------------------------------------------

type ViewMode = 'tiles' | 'flow';

const STORAGE_KEY = 'briefing-view-mode';

function getStoredViewMode(): ViewMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'tiles' || stored === 'flow') return stored;
  } catch {
    // localStorage unavailable (private browsing)
  }
  return 'tiles';
}

function setStoredViewMode(mode: ViewMode): void {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // localStorage unavailable (private browsing)
  }
}

// ---------------------------------------------------------------------------
// BriefingView — main wrapper
// ---------------------------------------------------------------------------

interface BriefingViewProps {
  briefingId: string;
  companyName: string;
  createdAt: string; // ISO date string
  resultMarkdown: string;
  /** QA score 0–10, null if QA didn't run */
  qaScore?: number | null;
  /** True if quota was automatically refunded due to low score */
  qaRefunded?: boolean;
  /** Whether PDF download is enabled (feature flag pdf_download_enabled) */
  pdfDownloadEnabled?: boolean;
  /** Demo mode: PDF button redirects to /register, print button hidden */
  demoMode?: boolean;
  /** Briefing type for badge display */
  briefingType?: BriefingType;
  /** UI language (users.language), defaults to German */
  language?: UiLanguage;
}

export function BriefingView({
  briefingId,
  companyName,
  createdAt,
  resultMarkdown,
  qaScore,
  qaRefunded,
  pdfDownloadEnabled = true,
  demoMode = false,
  briefingType,
  language = 'de',
}: BriefingViewProps) {
  const t = getBriefingViewMessages(language);
  const router = useRouter();
  const [viewMode, setViewMode] = useState<ViewMode>('tiles');
  const [isPdfLoading, setIsPdfLoading] = useState(false);

  // Restore view mode from localStorage on mount
  useEffect(() => {
    setViewMode(getStoredViewMode());
  }, []);

  const handleViewModeChange = useCallback((mode: ViewMode) => {
    setViewMode(mode);
    setStoredViewMode(mode);
  }, []);

  // Parse sections from markdown
  const sections = useMemo(
    () => parseBriefingSections(resultMarkdown, t.introduction),
    [resultMarkdown, t.introduction]
  );

  // Format date for display
  const formattedDate = useMemo(() => {
    try {
      return new Date(createdAt).toLocaleDateString(language === 'en' ? 'en-GB' : 'de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return createdAt;
    }
  }, [createdAt, language]);

  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  const handlePdfDownload = useCallback(async () => {
    // In demo mode, redirect to registration instead of downloading
    if (demoMode) {
      router.push('/register?ref=demo-pdf');
      return;
    }
    if (isPdfLoading) return;
    setIsPdfLoading(true);
    try {
      const response = await fetch(`/api/briefings/${briefingId}/pdf`);
      if (!response.ok) {
        let message: string = t.pdfFailed;
        try {
          const data = await response.json() as { error?: string };
          if (data.error) message = data.error;
        } catch {
          // ignore JSON parse failure
        }
        toast.error(message);
        return;
      }
      // Trigger browser download
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Use server-provided filename from Content-Disposition if available
      const cd = response.headers.get('Content-Disposition');
      const match = cd?.match(/filename="([^"]+)"/);
      a.download = match?.[1] ?? 'Briefing.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[BriefingView] PDF download error:', err);
      toast.error(t.pdfFailedRetry);
    } finally {
      setIsPdfLoading(false);
    }
  }, [briefingId, isPdfLoading, demoMode, router, t]);

  return (
    <div className="briefing-page flex flex-col h-[calc(100vh-2rem)] h-[calc(100dvh-2rem)] md:h-[calc(100vh-3rem)] md:h-[calc(100dvh-3rem)]" data-view-mode={viewMode}>
      {/* Print header — only visible when printing */}
      <div className="hidden print:block print:mb-6">
        <h1 className="text-2xl font-bold">{companyName}</h1>
        <p className="text-sm text-muted-foreground">
          {briefingType && BRIEFING_TYPES[briefingType]
            ? `${BRIEFING_TYPES[briefingType].label} | `
            : ''}
          {t.createdOn} {formattedDate}
        </p>
      </div>

      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-6 print:hidden flex-shrink-0">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold text-foreground break-words min-w-0">{companyName}</h1>
          <div className="flex flex-wrap gap-2 mt-2">
            {briefingType && (
              <BriefingTypeBadge type={briefingType} />
            )}
            <Badge variant="secondary">{t.created} {formattedDate}</Badge>
            {qaScore !== null && qaScore !== undefined && (
              <Badge
                className={
                  qaScore >= 7
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                    : qaScore >= 4
                      ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300'
                      : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
                }
              >
                {t.quality} {qaScore}/10
              </Badge>
            )}
          </div>
          {/* PROJ-33: Legal info bar — web-only, not in Markdown/PDF */}
          <p className="flex items-center gap-1.5 mt-2 text-sm text-muted-foreground">
            <Info className="h-3.5 w-3.5 shrink-0" />
            {t.legalBar}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* View toggle */}
          <TooltipProvider>
            <div className="flex items-center rounded-lg border border-border p-0.5">
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => handleViewModeChange('tiles')}
                    className={cn(
                      'flex items-center justify-center h-11 w-11 sm:h-8 sm:w-8 rounded-md transition-colors',
                      viewMode === 'tiles'
                        ? 'bg-accent text-accent-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    )}
                    aria-label={t.tileView}
                    aria-pressed={viewMode === 'tiles'}
                  >
                    <LayoutGrid className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>{t.tileView}</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => handleViewModeChange('flow')}
                    className={cn(
                      'flex items-center justify-center h-11 w-11 sm:h-8 sm:w-8 rounded-md transition-colors',
                      viewMode === 'flow'
                        ? 'bg-accent text-accent-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    )}
                    aria-label={t.flowView}
                    aria-pressed={viewMode === 'flow'}
                  >
                    <FileText className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>{t.flowView}</TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>

          {/* Action buttons — print hidden in demo mode */}
          {!demoMode && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePrint}
                    className="min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0"
                  >
                    <Printer className="h-3.5 w-3.5 mr-1" />
                    {t.print}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t.printTooltip}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {/* PDF download button */}
          {pdfDownloadEnabled && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={handlePdfDownload}
                    disabled={isPdfLoading}
                    aria-label={t.pdfDownload}
                    className="min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0"
                  >
                    {isPdfLoading ? (
                      <>
                        <svg
                          className="h-3.5 w-3.5 mr-1 animate-spin"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                          />
                        </svg>
                        PDF...
                      </>
                    ) : (
                      <>
                        <Download className="h-3.5 w-3.5 mr-1" />
                        PDF
                      </>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t.pdfDownload}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>

      {/* Refund notice — permanent, shown when quota was automatically refunded */}
      {qaRefunded && qaScore !== null && qaScore !== undefined && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20 px-4 py-3 text-xs sm:text-sm text-amber-800 dark:text-amber-300 print:hidden flex-shrink-0 mb-4 break-words">
          {t.refundNotice(qaScore)}
        </div>
      )}

      {/* Main content area with sidebar */}
      <div className="flex gap-6 flex-1 min-h-0">
        <BriefingSidebar sections={sections} language={language} />

        <div className="flex-1 min-w-0 overflow-y-auto">
          {viewMode === 'tiles' ? (
            <BriefingTileView sections={sections} />
          ) : (
            <BriefingFlowView sections={sections} />
          )}
        </div>
      </div>
    </div>
  );
}
